import React, {forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import Link from 'next/link';
import {MobileMenuData} from '@/util/menu-links';
import logoHover from 'public/images/nav-bar/logo-hover.webp';
import useSmoothScrollLock from '@/hooks/useSmoothScrollLock';
import Icon from '@/components/atoms/Icon';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './MobileDrawer.module.scss';

const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');

// The nesting depth at which a branch stops being collapsible and renders permanently open:
// levels 0 (Services) and 1 (Software Development) collapse, the category level shows its
// links straight away, so reaching a leaf costs two taps.
const ALWAYS_OPEN_DEPTH = 2;

// The menu DOM mounts this long after load (or immediately on the first tap), keeping it out
// of the initial render as the MUI version did.
const BACKGROUND_MOUNT_MS = 8000;

interface MenuNode {
    heading: string;
    path?: string | null;
    Children?: MenuNode[];
}

export interface MobileDrawerHandle {
    open: () => void;
}

/** Locks page scrolling while open, compensating for the scrollbar like MUI's modal did. */
const useBodyScrollLock = (locked: boolean) => {
    useEffect(() => {
        if (!locked) return undefined;
        const {body, documentElement} = document;
        const scrollbar = globalThis.innerWidth - documentElement.clientWidth;
        const previous = {overflow: body.style.overflow, paddingRight: body.style.paddingRight};
        if (scrollbar > 0) {
            const current = Number.parseFloat(globalThis.getComputedStyle(body).paddingRight) || 0;
            body.style.paddingRight = `${current + scrollbar}px`;
        }
        body.style.overflow = 'hidden';
        return () => {
            body.style.overflow = previous.overflow;
            body.style.paddingRight = previous.paddingRight;
        };
    }, [locked]);
};

const MobileDrawer = forwardRef<MobileDrawerHandle>(function MobileDrawer(_props, ref) {
    const [isOpen, setIsOpen] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
    const paperRef = useRef<HTMLDivElement>(null);
    const returnFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => setIsMounted(true), BACKGROUND_MOUNT_MS);
        return () => clearTimeout(timer);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);

    useImperativeHandle(ref, () => ({
        open: () => {
            returnFocusRef.current = document.activeElement as HTMLElement | null;
            if (isMounted) {
                setIsOpen(true);
                return;
            }
            // First tap before the background mount: mount now, then open on the next frame
            // so the slide-in still animates.
            setIsMounted(true);
            setTimeout(() => setIsOpen(true), 50);
        }
    }));

    useSmoothScrollLock(isOpen);
    useBodyScrollLock(isOpen);

    // Escape closes; focus moves into the drawer on open and back to the trigger on close.
    useEffect(() => {
        if (!isOpen) return undefined;
        paperRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            returnFocusRef.current?.focus?.();
        };
    }, [isOpen, close]);

    const toggle = (key: string) => setExpandedMap((prev) => ({...prev, [key]: !prev[key]}));

    const renderMenuItem = (item: MenuNode, key: string, depth = 0): React.ReactNode => {
        const hasChildren = Array.isArray(item?.Children) && item.Children.length > 0;
        const isExpanded = !!expandedMap[key];

        if (!hasChildren) {
            return (
                <div key={key} className={`mobile-accordian-menu-wrapper ${styles.leafWrapper}`}>
                    <Link
                        className={`mobile-accordian-menu-link ${styles.leafLink}`}
                        href={item.path ?? '#'}
                        onClick={close}
                        prefetch={false}
                    >
                        {item.heading}
                    </Link>
                </div>
            );
        }

        if (depth >= ALWAYS_OPEN_DEPTH) {
            return (
                <div key={key} className={styles.staticGroup}>
                    <div className={`acc-sub-main-heading ${styles.groupHeading} ${styles.staticHeading}`}>{item.heading}</div>
                    <div className={styles.staticChildren}>
                        {item.Children?.map((child, index) => renderMenuItem(child, `${key}-${index}`, depth + 1))}
                    </div>
                </div>
            );
        }

        const regionId = `drawer-region-${key}`;
        return (
            <div key={key} className={`drawer-menu-acc ${styles.accordion}`}>
                <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    aria-controls={regionId}
                    className={`drawer-menu-summary ${styles.summary}`}
                    onClick={() => toggle(key)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggle(key);
                        }
                    }}
                >
                    <div className={styles.summaryContent}>
                        <div className={`acc-sub-main-heading ${styles.groupHeading}`}>{item.heading}</div>
                    </div>
                    <div className={cx(styles.expandIcon, isExpanded && styles.expandIconOpen)}>
                        <Icon name={isExpanded ? 'Remove' : 'Add'} fontSize="inherit" />
                    </div>
                </div>
                <div id={regionId} role="region" className={cx(styles.collapse, isExpanded && styles.collapseOpen)}>
                    <div className={styles.collapseInner}>
                        <div className={styles.details}>
                            {item.Children?.map((child, index) => renderMenuItem(child, `${key}-${index}`, depth + 1))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (!isMounted) return null;

    return (
        <div className={cx(styles.modal, !isOpen && styles.modalHidden)} aria-hidden={!isOpen} role="presentation">
            <div className={cx(styles.backdrop, isOpen && styles.backdropOpen)} aria-hidden="true" onClick={close} />
            <div ref={paperRef} tabIndex={-1} className={cx(styles.paper, isOpen && styles.paperOpen)}>
                <div className={styles.content}>
                    <div className={styles.container}>
                        <div className={styles.grid}>
                            <div className={`${styles.gridItem} ${styles.headerItem}`}>
                                <div className={`mobile-header ${styles.mobileHeader}`}>
                                    <div className="debut-logo">
                                        <Link href="/" onClick={close}>
                                            <OptimizedImage
                                                src={logoHover}
                                                alt="Debut infotech"
                                                width={195}
                                                height={38}
                                                className="debut-logo"
                                                priority
                                            />
                                        </Link>
                                    </div>
                                    <div className="cross-icon">
                                        <button
                                            type="button"
                                            onClick={close}
                                            className={`close-icon-button ${styles.iconButton}`}
                                            aria-label="close"
                                        >
                                            <Icon name="Close" fontSize="inherit" className={styles.closeIcon} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className={`${styles.gridItem} ${styles.menuItem}`}>
                                {(MobileMenuData as MenuNode[])?.map((item, index) => (
                                    <Fragment key={item.heading}>
                                        {item?.Children ? (
                                            <div>{renderMenuItem(item, `root-${index}`)}</div>
                                        ) : (
                                            <div className="drawer-link-menu">
                                                <div className="menu-main-heading-links">
                                                    <Link href={item.path ?? '#'} prefetch={false}>
                                                        <div
                                                            className={`mui-header-menu-drawer-links ${styles.drawerLink}`}
                                                            onClick={close}
                                                        >
                                                            {item.heading}
                                                        </div>
                                                    </Link>
                                                </div>
                                            </div>
                                        )}
                                    </Fragment>
                                ))}
                            </div>
                            <div className={styles.gridItem}>
                                <div style={{flexGrow: 1}}>
                                    <Link href="/contact-us" className="menu-links" prefetch={false}>
                                        <button type="button" className={`helper-card-button ${styles.contactButton}`} onClick={close}>
                                            Contact Us
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default MobileDrawer;
